// Čist modul (klijentski bundle): better-auth greške kao poruke za korisnika.
// better-auth klijent vraća `{ code, message, status }` sa engleskom porukom
// ("User already exists."), koju korisnik nikad ne treba vidjeti. Poznati kod
// postaje i18n ključ `auth.errors.*`; poruka koju je naš server sam napisao na
// bosanskom (npr. suspendovan račun u `src/auth/index.ts`) prolazi netaknuta.

const CODE_KEYS: Record<string, string> = {
  USER_ALREADY_EXISTS: "auth.errors.userExists",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "auth.errors.userExists",
  INVALID_EMAIL_OR_PASSWORD: "auth.errors.invalidCredentials",
  INVALID_PASSWORD: "auth.errors.invalidPassword",
  PASSWORD_TOO_SHORT: "auth.errors.passwordTooShort",
  PASSWORD_TOO_LONG: "auth.errors.passwordTooLong",
  INVALID_EMAIL: "auth.errors.invalidEmail",
  EMAIL_NOT_VERIFIED: "auth.errors.emailNotVerified",
  USER_NOT_FOUND: "auth.errors.userNotFound",
  CREDENTIAL_ACCOUNT_NOT_FOUND: "auth.errors.noPasswordAccount",
  SESSION_EXPIRED: "auth.errors.sessionExpired",
  SESSION_NOT_FRESH: "auth.errors.sessionExpired",
  OTP_EXPIRED: "auth.errors.otpExpired",
  INVALID_OTP: "auth.errors.otpInvalid",
  TOO_MANY_ATTEMPTS: "auth.errors.otpTooManyAttempts",
};

const RATE_LIMITED_KEY = "auth.errors.rateLimited";

// Naša poruka: ima naša slova ili tipične bosanske riječi. Engleska poruka
// better-autha ili browsera ("Failed to fetch") ne prolazi.
const BOSNIAN_MESSAGE = /[čćžšđ]|\b(je|nije|vaš|vas|račun|nalog|molimo|pokušajte|kontaktirajte)\b/i;

export type AuthErrorResult = { key: string } | { text: string } | null;

function field(err: unknown, name: string): unknown {
  return typeof err === "object" && err !== null ? (err as Record<string, unknown>)[name] : undefined;
}

/** Ključ za poznati kod, bosanski tekst sa servera, ili null (koristi zamjensku poruku). */
export function resolveAuthError(err: unknown): AuthErrorResult {
  const code = field(err, "code");
  if (typeof code === "string" && CODE_KEYS[code]) return { key: CODE_KEYS[code]! };
  if (field(err, "status") === 429) return { key: RATE_LIMITED_KEY };

  const message = field(err, "message");
  if (typeof message === "string" && message.trim() && BOSNIAN_MESSAGE.test(message)) {
    return { text: message.trim() };
  }
  return null;
}

/**
 * Poruka za prikaz. `overrides` mijenja ključ za određeni kod na jednom ekranu
 * (npr. login ima svoj duži tekst za pogrešnu lozinku).
 */
export function authErrorMessage(
  err: unknown,
  t: (key: string) => string,
  fallbackKey: string,
  overrides: Record<string, string> = {},
): string {
  const code = field(err, "code");
  if (typeof code === "string" && overrides[code]) return t(overrides[code]!);
  const resolved = resolveAuthError(err);
  if (!resolved) return t(fallbackKey);
  return "key" in resolved ? t(resolved.key) : resolved.text;
}

/** Ograničenja lozinke u better-auth konfiguraciji (zadane vrijednosti, `src/auth/index.ts`). */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;
